import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { combineLatest, Observable, Subscription } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { map, switchMap, take } from 'rxjs/operators';

import {
  ApisDocumentData,
  FinnairAdvancePassengerInformationRequestV2,
  FinnairApisRequirementDocument,
  FinnairApisRequirementKeyEnum,
  FinnairOrder,
  FinnairTravelDocumentType,
} from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';
import { APIS_ENTRY_DOCUMENT_NUMBER_PATTERN, APIS_NAME_PATTERN, LocalDate } from '@fcom/core/utils';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { ButtonType } from '@fcom/ui-components/components/buttons';
import { SelectOption } from '@fcom/common/interfaces';
import { titleToGender } from '@fcom/common-booking/utils/common-booking.utils';
import { finShare } from '@fcom/rx/operators/finShare';

import { ManageBookingService } from '../../../../services';
import { FinnairPassengerItemWithApis } from '../../../check-in/interface/check-in.interface';
import { getFirstDefinedDateOrEmpty } from '../apis-utils';

enum EntryDocumentType {
  VISA = 'visa',
  RESIDENT = 'permanentResidentCard',
}

enum EntryDocumentTitle {
  VISA,
  ENTRY_DOCUMENT,
}

@Component({
  selector: 'fin-apis-visa',
  templateUrl: 'apis-visa.component.html',
  styleUrls: ['./../apis-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApisVisaComponent implements OnInit {
  readonly NotificationTheme = NotificationTheme;
  readonly LocalDate = LocalDate;
  readonly DatepickerTheme = DatepickerTheme;
  readonly ButtonType = ButtonType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly EntryDocumentTitle = EntryDocumentTitle;

  @Input()
  order$: Observable<FinnairOrder>;
  @Input()
  boundId$: Observable<string>;
  @Input()
  passenger$: Observable<FinnairPassengerItemWithApis>;
  @Input()
  nationalities$: Observable<SelectOption[]>;
  @Input()
  countries$: Observable<SelectOption[]>;
  @Input()
  genders$: Observable<SelectOption[]>;

  @Output() completed = new EventEmitter<void>();

  entryDocumentForm: FormGroup<{
    type: FormControl<string>;
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    birthDate: FormControl<LocalDate>;
    gender: FormControl<string>;
    number: FormControl<string>;
    nationality: FormControl<string>;
    issuingCountry: FormControl<string>;
    issuanceDate?: FormControl<LocalDate>;
    expiryDate: FormControl<LocalDate>;
  }>;

  entryDocumentTypes$: Observable<SelectOption[]>;
  requiredEntryDocumentType$: Observable<EntryDocumentType>;
  entryDocumentTitle$: Observable<EntryDocumentTitle>;
  showNationalitySelector$: Observable<boolean>;
  loading = false;

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private manageBookingService: ManageBookingService
  ) {}

  ngOnInit(): void {
    this.showNationalitySelector$ = combineLatest([this.order$, this.boundId$]).pipe(
      map(([order, boundId]) => {
        return order.eligibilities?.checkIn?.find((e) => e.id === boundId)?.isCarrierConnect ?? false;
      }),
      finShare()
    );

    this.requiredEntryDocumentType$ = this.passenger$.pipe(
      map((passenger) => {
        const visaRequirementPresent = passenger.apisCpr?.requirements?.some(
          (r) => r.key === FinnairApisRequirementKeyEnum.VISA
        );
        const residentCardRequirementPresent = passenger.apisCpr?.requirements?.some(
          (r) => r.key === FinnairApisRequirementKeyEnum.PERMANENT_RESIDENT_CARD
        );
        return visaRequirementPresent
          ? residentCardRequirementPresent
            ? undefined
            : EntryDocumentType.VISA
          : residentCardRequirementPresent
            ? EntryDocumentType.RESIDENT
            : undefined;
      })
    );
    this.entryDocumentTitle$ = this.requiredEntryDocumentType$.pipe(
      map((type) => (type === EntryDocumentType.VISA ? EntryDocumentTitle.VISA : EntryDocumentTitle.ENTRY_DOCUMENT))
    );
    this.entryDocumentTypes$ = this.languageService.translate('MMB.apis.passenger').pipe(
      map((types) => {
        return [EntryDocumentType.VISA, EntryDocumentType.RESIDENT].map((type) => ({
          name: types[type] ?? type,
          value: type,
        }));
      })
    );
    this.subscriptions.add(
      combineLatest([this.requiredEntryDocumentType$, this.passenger$, this.order$, this.boundId$])
        .pipe(take(1))
        .subscribe(([type, passenger, order, boundId]) => {
          const passportData: FinnairApisRequirementDocument = passenger.apisCpr?.requirements?.find(
            (requirement) => requirement.key === FinnairApisRequirementKeyEnum.PASSPORT
          )?.data as FinnairApisRequirementDocument;
          const entryDocumentData: FinnairApisRequirementDocument = passenger.apisCpr?.requirements?.find(
            (requirement) =>
              requirement.key ===
              (type === EntryDocumentType.VISA
                ? FinnairApisRequirementKeyEnum.VISA
                : FinnairApisRequirementKeyEnum.PERMANENT_RESIDENT_CARD)
          )?.data as FinnairApisRequirementDocument;
          this.entryDocumentForm = new FormGroup({
            type: new FormControl(type ?? '', Validators.required),
            firstName: new FormControl(entryDocumentData?.firstName ?? passenger.firstName, [
              Validators.required,
              Validators.minLength(2),
              Validators.maxLength(39),
              Validators.pattern(APIS_NAME_PATTERN),
            ]),
            lastName: new FormControl(entryDocumentData?.lastName ?? passenger.lastName, [
              Validators.required,
              Validators.minLength(2),
              Validators.maxLength(39),
              Validators.pattern(APIS_NAME_PATTERN),
            ]),
            birthDate: new FormControl(
              getFirstDefinedDateOrEmpty(
                entryDocumentData?.dateOfBirth,
                passportData?.dateOfBirth,
                passenger.dateOfBirth
              ),
              Validators.required
            ),
            gender: new FormControl(
              entryDocumentData?.gender ??
                passportData?.gender ??
                passenger?.gender ??
                titleToGender(passenger.title) ??
                '',
              Validators.required
            ),
            nationality: new FormControl(
              passportData?.nationality ?? entryDocumentData?.nationality ?? '',
              Validators.required
            ),
            number: new FormControl(entryDocumentData?.number ?? '', [
              Validators.required,
              Validators.maxLength(20),
              Validators.pattern(APIS_ENTRY_DOCUMENT_NUMBER_PATTERN),
            ]),
            issuingCountry: new FormControl(entryDocumentData?.issuingCountry ?? '', Validators.required),
            expiryDate: new FormControl(getFirstDefinedDateOrEmpty(entryDocumentData?.expiryDate), Validators.required),
          });

          if (order.eligibilities.checkIn?.find((e) => e.id === boundId)?.isCarrierConnect) {
            this.entryDocumentForm.addControl(
              'issuanceDate',
              new FormControl(getFirstDefinedDateOrEmpty(passportData?.issuanceDate), Validators.required)
            );
          }
        })
    );
  }

  saveAndContinue(): void {
    if (this.entryDocumentForm.dirty && this.entryDocumentForm.valid) {
      this.entryDocumentForm.disable();
      this.loading = true;

      this.subscriptions.add(
        this.createEntryDocumentUpdateRequest()
          .pipe(
            take(1),
            switchMap((request) => this.manageBookingService.updateRegulatoryDetails(request))
          )
          .subscribe({
            next: () => {
              this.entryDocumentForm.enable();
              this.loading = false;
              this.completed.emit();
            },
            error: () => {
              this.entryDocumentForm.enable();
              this.loading = false;
            },
          })
      );
    } else if (this.entryDocumentForm.valid) {
      this.completed.emit();
    } else {
      this.markFormAsTouched();
    }
  }

  private markFormAsTouched(): void {
    Object.keys(this.entryDocumentForm.controls).forEach((field) => {
      const control = this.entryDocumentForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }
  private createEntryDocumentUpdateRequest(): Observable<FinnairAdvancePassengerInformationRequestV2[]> {
    return combineLatest([this.boundId$, this.passenger$]).pipe(
      map(([boundId, passenger]) => {
        let apisDocument;
        try {
          apisDocument = {
            documentType:
              this.entryDocumentForm.controls.type.value === EntryDocumentType.VISA
                ? FinnairTravelDocumentType.VISA
                : FinnairTravelDocumentType.PERMANENTRESIDENTCARD,
            expirationDate: this.entryDocumentForm.controls.expiryDate.value.toString(),
            firstName: this.entryDocumentForm.controls.firstName.value.toUpperCase(),
            lastName: this.entryDocumentForm.controls.lastName.value.toUpperCase(),
            issuingCountry: this.entryDocumentForm.controls.issuingCountry.value,
            gender: this.entryDocumentForm.controls.gender.value,
            dateOfBirth: this.entryDocumentForm.controls.birthDate.value.toString(),
            nationality: this.entryDocumentForm.controls.nationality.value,
            number: this.entryDocumentForm.controls.number.value.toUpperCase(),
            ...(this.entryDocumentForm.controls.issuanceDate?.value
              ? { issuanceDate: this.entryDocumentForm.controls.issuanceDate?.value.toString() }
              : {}),
          } as ApisDocumentData;
        } catch (e) {
          console.log('error', e);
        }
        const type = this.entryDocumentForm.controls.type.value === EntryDocumentType.VISA ? 'visa' : 'residentCard';

        return [
          {
            advancePassengerInformation: [
              {
                [type]: apisDocument,
                travelerId: passenger.id,
              },
            ],
            boundId,
          },
        ];
      }),
      take(1)
    );
  }
}
